import React from "react";
import { motion } from "framer-motion";
import { Plus, Eye, EyeOff, Search, X } from "lucide-react";
import TipsSection from "./TipsSection";
import SendersList from "./SendersList";
import SenderTable from "./SenderTable";

const SenderManagementContent = ({
  senderIds,
  currentSenderIds,
  currentPage,
  totalPages,
  startIndex,
  endIndex,
  itemsPerPage,
  handlePageChange,
  searchQuery,
  setSearchQuery,
  showTips,
  setShowTips,
  setShowAddForm,
  showAddForm,
  setShowAddForm: setShowAddFormProp,
  newSenderId,
  setNewSenderId,
  errors,
  setErrors,
  handleAddSenderId,
  isAddingSender,
  editingId,
  editValue,
  setEditValue,
  handleEditSenderId,
  cancelEditing,
  startEditing,
  copyToClipboard,
  openDeleteModal,
  showDeleteModal,
  closeDeleteModal,
  senderToDelete,
  handleDeleteSenderId,
  isDeletingSender,
  getStatusColor,
  viewMode,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="overflow-hidden rounded-3xl border shadow-2xl backdrop-blur-xl bg-white/80 border-white/20"
    >
      {/* Header with Add Button */}
      <div className="p-4 border-b sm:p-6 lg:p-8 border-white/20">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="mb-2 text-xl font-bold text-gray-900 sm:text-2xl">
              Your Sender IDs
            </h2>
            <p className="text-gray-600">
              Manage and monitor your sender identities
            </p>
          </div>
          <div className="flex flex-col gap-3 items-start sm:flex-row sm:items-center">
            {/* Search Input */}
            <div className="relative w-full sm:w-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search sender IDs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full sm:w-64 pl-10 pr-10 py-2.5 border border-white/30 rounded-xl text-sm bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowTips(!showTips)}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 border border-white/30 rounded-xl text-sm font-medium text-gray-700 bg-white/80 backdrop-blur-sm hover:bg-white/90 transition-all duration-200 shadow-sm"
            >
              {showTips ? (
                <EyeOff className="mr-2 w-4 h-4" />
              ) : (
                <Eye className="mr-2 w-4 h-4" />
              )}
              {showTips ? "Hide Tips" : "Show Tips"}
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex justify-center items-center px-6 py-3 w-full font-semibold text-white bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl shadow-lg transition-all duration-200 transform sm:w-auto hover:from-purple-700 hover:to-blue-700 hover:shadow-xl hover:scale-105 active:scale-95"
            >
              <Plus className="mr-2 w-5 h-5" />
              <span className="hidden sm:inline">Add Sender ID</span>
              <span className="sm:hidden">Add New</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tips Section */}
      <TipsSection showTips={showTips} />

      {/* Sender IDs List */}
      {viewMode === 'table' ? (
        <SenderTable
          senderIds={senderIds}
          currentPage={currentPage}
          totalPages={totalPages}
          startIndex={startIndex}
          endIndex={endIndex}
          itemsPerPage={itemsPerPage}
          handlePageChange={handlePageChange}
          editingId={editingId}
          editValue={editValue}
          setEditValue={setEditValue}
          errors={errors}
          setErrors={setErrors}
          handleEditSenderId={handleEditSenderId}
          cancelEditing={cancelEditing}
          startEditing={startEditing}
          copyToClipboard={copyToClipboard}
          openDeleteModal={openDeleteModal}
          getStatusColor={getStatusColor}
        />
      ) : (
        <SendersList
          senderIds={senderIds}
          currentSenderIds={currentSenderIds}
          currentPage={currentPage}
          totalPages={totalPages}
          startIndex={startIndex}
          endIndex={endIndex}
          itemsPerPage={itemsPerPage}
          handlePageChange={handlePageChange}
          setShowAddForm={setShowAddForm}
          editingId={editingId}
          editValue={editValue}
          setEditValue={setEditValue}
          errors={errors}
          setErrors={setErrors}
          handleEditSenderId={handleEditSenderId}
          cancelEditing={cancelEditing}
          startEditing={startEditing}
          copyToClipboard={copyToClipboard}
          openDeleteModal={openDeleteModal}
          getStatusColor={getStatusColor}
        />
      )}
    </motion.div>
  );
};

export default SenderManagementContent; 